import { Suspense, lazy, useEffect, useState } from 'react';
import { Alert, Button, Icons, Spinner } from '../../components/ui.tsx';
import { useIsSmallScreen } from '../../lib/useMediaQuery.ts';
import { linkSource } from '../../lib/sheet-source.ts';

/**
 * A sheet or a letter, opened by somebody with no account.
 *
 * The app's own editor, read-only, rather than a PDF of it. Converting first
 * was the easy thing and the wrong one: it flattens a workbook into a picture
 * of a workbook — no tabs, no widths, no formats, nothing selectable — and a
 * letter into a page nobody can copy a line out of. What was shared was the
 * work, so what is shown is the work.
 *
 * Read-only is not this component's doing and must never be. There is no
 * session here, so the editor is built with writing switched off, and the guest
 * routes carry no way to save a cell or a paragraph even if it were not. This
 * page only declines to draw the buttons.
 *
 * Both editors are loaded on demand: they are the largest code in the system
 * and a guest who only wanted a PDF out of a folder should not pay for them
 * until they open one.
 */

const SheetReader = lazy(() =>
  import('../sheets/SheetPage.tsx').then((module) => ({ default: module.SheetEditorView })),
);

const DocumentReader = lazy(() =>
  import('../documents/DocumentPage.tsx').then((module) => ({ default: module.DocumentView })),
);

export interface GuestRecord {
  id: string;
  name: string;
  kind: 'document' | 'spreadsheet';
}

export function GuestRecordViewer({
  src,
  downloadHref,
  name,
  kind,
  onClose,
}: {
  /** Where the record comes from. The two kinds of link spell it differently. */
  src: string;
  /** Absent when the link does not allow a copy. */
  downloadHref?: string | undefined;
  name: string;
  kind: 'document' | 'spreadsheet';
  /** Absent when there is nothing behind this page to go back to. */
  onClose?: (() => void) | undefined;
}) {
  const [record, setRecord] = useState<unknown>(null);
  const [failed, setFailed] = useState(false);
  const small = useIsSmallScreen();

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const response = await fetch(src, { credentials: 'omit' });
        if (!response.ok) throw new Error('gone');
        const loaded: unknown = await response.json();
        if (!cancelled) setRecord(loaded);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [src]);

  useEffect(() => {
    if (!onClose) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-canvas">
      <header className="flex shrink-0 items-center gap-3 border-b border-border-strong bg-surface px-4 py-2">
        <p className="min-w-0 flex-1 truncate text-body-sm font-medium text-ink">{name}</p>

        {/*
          The copy is the real file — a .docx or an .xlsx, named by the server
          — because a link to a workbook that hands back a PDF has handed back
          something nobody can open in Excel.
        */}
        {downloadHref ? (
          <a href={downloadHref}>
            <Button icon={<Icons.Download />}>Download</Button>
          </a>
        ) : null}

        {onClose ? (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-2 text-muted transition-fast hover:bg-sunken hover:text-ink"
          >
            <Icons.Close size={20} />
          </button>
        ) : null}
      </header>

      <div className="min-h-0 flex-1 overflow-hidden p-2">
        {failed ? (
          <div className="flex h-full items-center justify-center">
            <Alert>That could not be opened.</Alert>
          </div>
        ) : small ? (
          /*
            The same rule the app applies to its own editors, said for somebody
            who has no account to go back to: a virtualised grid on a phone
            screen is mobile data spent on something unreadable. The copy, if
            the link allows one, opens in whatever they already have.
          */
          <div className="flex h-full flex-col items-center justify-center px-6 text-center">
            <h2 className="text-body font-medium text-ink">
              {kind === 'spreadsheet' ? 'Spreadsheets' : 'Documents'} need a larger screen
            </h2>
            <p className="mt-2 max-w-xs text-body-sm text-muted">
              Open this link on a laptop, or a tablet in landscape, and it will be here waiting.
            </p>
            {downloadHref ? (
              <a className="mt-5" href={downloadHref}>
                <Button icon={<Icons.Download />}>Download a copy</Button>
              </a>
            ) : null}
          </div>
        ) : !record ? (
          <div className="flex h-full items-center justify-center">
            <Spinner label="Opening" />
          </div>
        ) : (
          <Suspense
            fallback={
              <div className="flex h-full items-center justify-center">
                <Spinner label="Opening" />
              </div>
            }
          >
            {kind === 'spreadsheet' ? (
              <SheetReader initial={record as never} reader source={linkSource(src)} />
            ) : (
              <DocumentReader initial={record as never} reader />
            )}
          </Suspense>
        )}
      </div>
    </div>
  );
}
